import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { COOKIE_NAMES, COUPON_STATUS, COUPON_TYPE, DISCOUNT_TYPE, ORDER_CATEGORY, TRAINING_PRODUCT_TYPES } from '../constants';
import { checkingTestMode, roundingNumber, tryParseJSON } from '.';
import { getDiscount } from '../components/CMS/Shared/SOWOrders/SOWOrder/utils';

const ECOMMERCE_EVENT = {
     ADD_TO_CART: 'add_to_cart',
     VIEW_CART: 'view_cart',
     BEGIN_CHECKOUT: 'begin_checkout',
     ADD_PAYMENT_INFO: 'add_payment_info',
     ADD_SHIPPING_INFO: 'add_shipping_info',
     PURCHASE: 'purchase',
};

const pushToDataLayerLL = (payload) => {
     const { cleanedEvent, coupon, discount } = payload;

     window.dataLayerLL = window.dataLayerLL || [];

     if (cleanedEvent.event === ECOMMERCE_EVENT.ADD_SHIPPING_INFO) {
          const lastShippingEvent = window.dataLayerLL.filter((e) => e.event === ECOMMERCE_EVENT.ADD_SHIPPING_INFO)?.pop();

          const isNotChange = lastShippingEvent?.shipping_tier === cleanedEvent.shipping_tier;
          if (isNotChange) return;
     }

     if (cleanedEvent.event === ECOMMERCE_EVENT.ADD_PAYMENT_INFO) {
          const lastPaymentEvent = window.dataLayerLL.filter((e) => e.event === ECOMMERCE_EVENT.ADD_PAYMENT_INFO)?.pop();

          const isNotChange = lastPaymentEvent?.payment_type === cleanedEvent.payment_type;
          if (isNotChange) return;
     }

     const isCouponOnCart = coupon?.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_CART;

     if (isCouponOnCart) {
          Object.assign(cleanedEvent, {
               cart_level_coupon: coupon.code,
               cart_level_discount: discount,
          });
     }

     if (!cleanedEvent.discount) delete cleanedEvent.discount;
     if (!cleanedEvent.coupon) delete cleanedEvent.coupon;

     //Clean items
     cleanedEvent.items.forEach((item) => {
          if (!item.discount) delete item.discount;
          if (!item.coupon) delete item.coupon;
     });

     window.dataLayerLL.push(cleanedEvent);

     // console.log(cleanedEvent.event, cleanedEvent);
};

const calculateTotalDiscountOnItem = (item, product, coupon) => {
     const { price, discount: productDiscount, discountType: productDiscountType, discountStartAt, discountEndAt } = product;

     const today = new dayjs();

     const isValidDiscount = productDiscount && discountStartAt && discountEndAt && today >= dayjs(discountStartAt) && today <= dayjs(discountEndAt);
     const discountFromProduct = productDiscount && isValidDiscount ? getDiscount(price, productDiscount, productDiscountType) : 0;

     let discountFromCoupon = 0;
     if (coupon) {
          const { type, discount, discountType, validProductIds } = coupon;
          const isCouponOnProduct = type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL;
          const isApplyCoupon = validProductIds?.length ? validProductIds.includes(product.id) : true;

          discountFromCoupon = isCouponOnProduct && isApplyCoupon ? getDiscount(price, discount, discountType) : 0;
     }

     const finalDiscount = (discountFromProduct + discountFromCoupon) * item.quantity;

     return roundingNumber(finalDiscount);
};

const calculateTotalDiscountOnCart = (items, totalCartDiscount, coupon) => {
     const totalDiscountAllItems = items.reduce((total, currentItem) => total + currentItem.discount, 0);

     const isCouponOnCart = coupon?.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_CART;

     let finalDiscount = totalDiscountAllItems;

     if (isCouponOnCart) finalDiscount = totalDiscountAllItems + totalCartDiscount;

     return roundingNumber(finalDiscount);
};

const calculateCartValues = (cartItems, coupon) => {
     if (!cartItems.length) return { total: 0, discount: 0 };

     let cartSubTotal = 0;

     cartItems.forEach((cartItem) => {
          const cartItemQuantity = cartItem.quantity || 1;
          const itemTotal = cartItem.price * cartItemQuantity;
          let itemDiscount = 0;

          if (cartItem.discount > 0) {
               itemDiscount =
                    cartItem.discountType === DISCOUNT_TYPE.PRICE
                         ? cartItem.discount * cartItemQuantity
                         : ((cartItem.price * cartItem.discount) / 100) * cartItemQuantity;
          }

          if (itemDiscount > itemTotal) {
               itemDiscount = itemTotal;
          }

          cartItem.total = itemTotal - itemDiscount;

          if (cartItem.total < 0) {
               cartItem.total = 0;
          }

          cartSubTotal += cartItem.total;
     });

     let cartDiscount = 0;
     const hasDiscountCoupon = coupon && coupon.discount > 0 && coupon.status === COUPON_STATUS.AVAILABLE;

     if (hasDiscountCoupon) {
          const validProductIds = coupon?.validProductIds || [];
          const isDiscountTypePrice = coupon.discountType === DISCOUNT_TYPE.PRICE;

          switch (coupon.type) {
               case COUPON_TYPE.DISCOUNT_AMOUNT_IN_CART:
                    if (isDiscountTypePrice) {
                         cartDiscount = cartItems.reduce((cartItemAccumulator, currentCartItem) => {
                              if (!validProductIds.length || validProductIds.includes(currentCartItem.productId)) {
                                   const itemCouponDiscount = coupon.discount > currentCartItem.total ? currentCartItem.total : coupon.discount;

                                   return cartItemAccumulator + itemCouponDiscount;
                              }

                              return cartItemAccumulator;
                         }, 0);

                         if (cartDiscount > coupon.discount) {
                              cartDiscount = coupon.discount;
                         }
                    } else {
                         const cartDiscountTotal =
                              cartItems.reduce((cartItemAccumulator, currentCartItem) => {
                                   if (!validProductIds.length || validProductIds.includes(currentCartItem.productId)) {
                                        return cartItemAccumulator + currentCartItem.total;
                                   }

                                   return cartItemAccumulator;
                              }, 0) || 1;

                         cartDiscount = (cartDiscountTotal * coupon.discount) / 100;
                    }
                    break;

               case COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL:
                    if (isDiscountTypePrice) {
                         cartDiscount = cartItems.reduce((cartItemAccumulator, currentCartItem) => {
                              if (!validProductIds.length || validProductIds.includes(currentCartItem.productId)) {
                                   let itemCouponDiscount = coupon.discount * currentCartItem.quantity;

                                   if (itemCouponDiscount > currentCartItem.total) {
                                        itemCouponDiscount = currentCartItem.total;
                                   }

                                   return cartItemAccumulator + itemCouponDiscount;
                              }

                              return cartItemAccumulator;
                         }, 0);
                    } else {
                         const cartDiscountTotal =
                              cartItems.reduce((cartItemAccumulator, currentCartItem) => {
                                   if (!validProductIds.length || validProductIds.includes(currentCartItem.productId)) {
                                        return cartItemAccumulator + currentCartItem.total;
                                   }

                                   return cartItemAccumulator;
                              }, 0) || 1;

                         cartDiscount = (cartDiscountTotal * coupon.discount) / 100;
                    }
                    break;

               default:
                    break;
          }
     }

     const discount = cartDiscount > cartSubTotal ? cartSubTotal : cartDiscount;
     const total = roundingNumber(cartSubTotal - discount);

     return { total, discount };
};

const checkingTestModeIncludeSOW = ({ user, order }) => {
     const isSOWOrder = order.category === ORDER_CATEGORY.SOW;

     if (isSOWOrder) {
          const isLiveSOWEnabled = Cookies.get(COOKIE_NAMES.SOW_ORDER_ENABLED) === '1';
          if (isLiveSOWEnabled) return false;

          const isTestSOWEnabled = Cookies.get(COOKIE_NAMES.TEST_SOW_ORDER_ENABLED) === '1';
          return isTestSOWEnabled;
     }

     return checkingTestMode(user);
};
//---------------------------------------------------------------------------

export const collectECEvent_addToCart_onPageProduct = (payload) => {
     const { addedProducts, user } = payload;
     if (!addedProducts.length) return;

     const isTestMode = checkingTestMode(user);
     if (isTestMode) return;

     const cleanedEvent = {
          event: ECOMMERCE_EVENT.ADD_TO_CART,
          currency: 'USD',
          value: 0,
          items: [],
     };

     let index = 1;
     const newItems = [];

     for (let i = 0; i < addedProducts.length; i++) {
          const item = addedProducts[i];
          const { sku, publishName, price, quantityCart } = item;

          const productType = item.productType.trainingProductType || null;
          const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          const isCreditSelected = item.selectedEventCredit && item.selectedEventCreditQuantity > 0;

          const newItem = {
               item_id: sku || null,
               item_name: publishName || null,
               index,
               item_category: productType || null,
               price: price || 0,
               quantity: quantityCart || 1,
          };

          //Normal Product Except Live Training
          if (!isLiveTraining) {
               newItems.push(newItem);
               index++;
          }

          //Normal Event
          const events = item.listEventSelected || [];
          for (const event of events) {
               const quantity = event.quantityEvent || 1;

               const eventName = item.events.find((e) => e.id === event.value)?.name || 'custom-date';

               const creditAddedItem = {
                    ...newItem,
                    item_category2: eventName,
                    quantity,
               };

               newItems.push(creditAddedItem);
               index++;
          }

          //Event Credit
          if (isCreditSelected) {
               const quantity = Number(item.selectedEventCreditQuantity || 0);

               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'event-credit',
                    quantity,
               };

               newItems.push(creditAddedItem);
               index++;
          }

          //Tiers
          const tiers = item.listTierSelected || [];
          for (const tier of tiers) {
               if (!tier.quantity) continue;

               for (let i = 0; i < tier.quantity; i++) {
                    const tierAddedItem = {
                         ...newItem,
                         index,
                         item_category2: `tier-${tier.name}` || null,
                         price: Number(tier.price || 0),
                         quantity: 1,
                    };

                    newItems.push(tierAddedItem);
                    index++;
               }
          }
     }

     if (newItems.length) {
          const value = newItems.reduce((totalPrice, currentItem) => totalPrice + currentItem.price * currentItem.quantity, 0) || 0;

          cleanedEvent.value = value;
          cleanedEvent.items = [...newItems];
     }

     pushToDataLayerLL({ cleanedEvent });

     return cleanedEvent;
};

export const collectECEvent_addToCart_onPageCart = (payload) => {
     const { cartItems, products, coupon, user } = payload;
     if (!cartItems.length) return;

     const isTestMode = checkingTestMode(user);
     if (isTestMode) return;

     const cleanedEvent = {
          event: ECOMMERCE_EVENT.ADD_TO_CART,
          currency: 'USD',
          value: 0,
          items: [],
     };

     const isCouponOnItem = coupon && coupon.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL;
     const validProductIds = coupon?.validProductIds || [];

     let index = 1;
     const newItems = [];

     for (let i = 0; i < cartItems.length; i++) {
          const item = cartItems[i];
          const { productId, eventId, isCredit, tierId, quantity } = item;

          const product = products.find((product) => product.id === productId);
          if (!product) continue;

          const couponOnItem = isCouponOnItem && (validProductIds.includes(productId) || !validProductIds.length) ? coupon.code : null;
          const itemDiscount = calculateTotalDiscountOnItem(item, product, coupon, []);

          const { sku, publishName, price } = product;
          const productType = product.productType.trainingProductType || null;
          const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          const isCustomDate = !eventId && !isCredit && !tierId;

          const newItem = {
               item_id: sku || null,
               item_name: publishName || null,
               coupon: couponOnItem,
               discount: itemDiscount || 0,
               index,
               item_category: productType || null,
               price: price || 0,
               quantity: quantity || 1,
          };

          //Normal Product Except Live Training
          if (!isLiveTraining) {
               newItems.push(newItem);
               index++;
               continue;
          }

          //Normal Event
          if (eventId) {
               const eventName = product.events.find((event) => event.id === eventId)?.name || null;

               const creditAddedItem = {
                    ...newItem,
                    item_category2: eventName,
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //CustomDate Event
          if (isCustomDate) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'custom-date',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Event Credit
          if (isCredit) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'event-credit',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Tiers
          if (tierId) {
               const pricingTiers = tryParseJSON(product?.pricingTiers) || [];
               const tier = pricingTiers.find((tier) => tier.id === tierId);

               for (let i = 0; i < quantity; i++) {
                    const tierAddedItem = {
                         ...newItem,
                         index,
                         item_category2: `tier-${tier.name}` || null,
                         price: Number(tier.price || 0),
                         quantity: 1,
                    };

                    newItems.push(tierAddedItem);
                    index++;
               }
          }
     }

     if (!newItems.length) return cleanedEvent;

     const { total, discount } = calculateCartValues(cartItems, coupon);

     cleanedEvent.value = total;
     cleanedEvent.items = [...newItems];
     cleanedEvent.discount = calculateTotalDiscountOnCart(newItems, discount, coupon);

     pushToDataLayerLL({ cleanedEvent, coupon, discount });

     return cleanedEvent;
};

export const collectECEvent_viewCart = (payload) => {
     const { cartItems, products, coupon, user } = payload;
     if (!cartItems.length) return;

     const isTestMode = checkingTestMode(user);
     if (isTestMode) return;

     const cleanedEvent = {
          event: ECOMMERCE_EVENT.VIEW_CART,
          currency: 'USD',
          discount: 0,
          value: 0,
          coupon: coupon?.code || null,
          items: [],
     };

     const isCouponOnItem = coupon && coupon.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL;
     const validProductIds = coupon?.validProductIds || [];

     let index = 1;
     const newItems = [];

     for (let i = 0; i < cartItems.length; i++) {
          const item = cartItems[i];
          const { productId, eventId, isCredit, tierId, quantity } = item;

          const product = products.find((product) => product.id === productId);
          if (!product) continue;

          const couponOnItem = isCouponOnItem && (validProductIds.includes(productId) || !validProductIds.length) ? coupon.code : null;
          const itemDiscount = calculateTotalDiscountOnItem(item, product, coupon, []);

          const { sku, publishName, price } = product;
          const productType = product.productType.trainingProductType || null;
          const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          const isCustomDate = !eventId && !isCredit && !tierId;

          const newItem = {
               item_id: sku || null,
               item_name: publishName || null,
               coupon: couponOnItem,
               discount: itemDiscount || 0,
               index,
               item_category: productType || null,
               price: price || 0,
               quantity: quantity || 1,
          };

          //Normal Product Except Live Training
          if (!isLiveTraining) {
               newItems.push(newItem);
               index++;
               continue;
          }

          //Normal Event
          if (eventId) {
               const eventName = product.events.find((event) => event.id === eventId)?.name || null;

               const creditAddedItem = {
                    ...newItem,
                    item_category2: eventName,
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //CustomDate Event
          if (isCustomDate) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'custom-date',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Event Credit
          if (isCredit) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'event-credit',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Tiers
          if (tierId) {
               const pricingTiers = tryParseJSON(product?.pricingTiers) || [];
               const tier = pricingTiers.find((tier) => tier.id === tierId);

               for (let i = 0; i < quantity; i++) {
                    const tierAddedItem = {
                         ...newItem,
                         index,
                         item_category2: `tier-${tier.name}` || null,
                         price: Number(tier.price || 0),
                         quantity: 1,
                    };

                    newItems.push(tierAddedItem);
                    index++;
               }
          }
     }

     if (!newItems.length) return cleanedEvent;

     const { total, discount } = calculateCartValues(cartItems, coupon);

     cleanedEvent.value = total;
     cleanedEvent.items = [...newItems];
     cleanedEvent.discount = calculateTotalDiscountOnCart(newItems, discount, coupon);

     pushToDataLayerLL({ cleanedEvent, coupon, discount });

     return cleanedEvent;
};

export const collectECEvent_beginCheckout = (payload) => {
     const { cart, cartItems, products, coupon, user } = payload;
     if (!cartItems.length) return;

     const isTestMode = checkingTestMode(user);
     if (isTestMode) return;

     const cleanedEvent = {
          event: ECOMMERCE_EVENT.BEGIN_CHECKOUT,
          currency: 'USD',
          discount: 0,
          value: cart.total || 0,
          coupon: coupon?.code || null,
          items: [],
     };

     const isCouponOnItem = coupon && coupon.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL;
     const validProductIds = coupon?.validProductIds || [];

     let index = 1;
     const newItems = [];

     for (let i = 0; i < cartItems.length; i++) {
          const item = cartItems[i];
          const { productId, eventId, isCredit, tierId, quantity } = item;

          const product = products.find((product) => product.id === productId);
          if (!product) continue;

          const couponOnItem = isCouponOnItem && (validProductIds.includes(productId) || !validProductIds.length) ? coupon.code : null;
          const itemDiscount = calculateTotalDiscountOnItem(item, product, coupon, []);

          const { sku, publishName, price } = product;
          const productType = product.productType.trainingProductType || null;
          const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          const isCustomDate = !eventId && !isCredit && !tierId;

          const newItem = {
               item_id: sku || null,
               item_name: publishName || null,
               coupon: couponOnItem,
               discount: itemDiscount || 0,
               index,
               item_category: productType || null,
               price: price || 0,
               quantity: quantity || 1,
          };

          //Normal Product Except Live Training
          if (!isLiveTraining) {
               newItems.push(newItem);
               index++;
               continue;
          }

          //Normal Event
          if (eventId) {
               const eventName = product.events.find((event) => event.id === eventId)?.name || null;

               const creditAddedItem = {
                    ...newItem,
                    item_category2: eventName,
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //CustomDate Event
          if (isCustomDate) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'custom-date',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Event Credit
          if (isCredit) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'event-credit',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Tiers
          if (tierId) {
               const pricingTiers = tryParseJSON(product?.pricingTiers) || [];
               const tier = pricingTiers.find((tier) => tier.id === tierId);

               for (let i = 0; i < quantity; i++) {
                    const tierAddedItem = {
                         ...newItem,
                         index,
                         item_category2: `tier-${tier.name}` || null,
                         price: Number(tier.price || 0),
                         quantity: 1,
                    };

                    newItems.push(tierAddedItem);
                    index++;
               }
          }
     }

     if (!newItems.length) return cleanedEvent;

     const { total, discount } = calculateCartValues(cartItems, coupon);

     cleanedEvent.value = total;
     cleanedEvent.items = [...newItems];
     cleanedEvent.discount = calculateTotalDiscountOnCart(newItems, discount, coupon);

     pushToDataLayerLL({ cleanedEvent, coupon, discount });

     return cleanedEvent;
};

export const collectECEvent_addPaymentInfo = (payload) => {
     const { cartItems, products, coupon, paymentType, user } = payload;
     if (!cartItems.length) return;

     const isTestMode = checkingTestMode(user);
     if (isTestMode) return;

     const cleanedEvent = {
          event: ECOMMERCE_EVENT.ADD_PAYMENT_INFO,
          currency: 'USD',
          discount: 0,
          value: 0,
          coupon: coupon?.code || null,
          payment_type: paymentType || null,
          items: [],
     };

     const isCouponOnItem = coupon && coupon.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL;
     const validProductIds = coupon?.validProductIds || [];

     let index = 1;
     const newItems = [];

     for (let i = 0; i < cartItems.length; i++) {
          const item = cartItems[i];
          const { productId, eventId, isCredit, tierId, quantity } = item;

          const product = products.find((product) => product.id === productId);
          if (!product) continue;

          const couponOnItem = isCouponOnItem && (validProductIds.includes(productId) || !validProductIds.length) ? coupon.code : null;
          const itemDiscount = calculateTotalDiscountOnItem(item, product, coupon, []);

          const { sku, publishName, price } = product;
          const productType = product.productType.trainingProductType || null;
          const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          const isCustomDate = !eventId && !isCredit && !tierId;

          const newItem = {
               item_id: sku || null,
               item_name: publishName || null,
               coupon: couponOnItem,
               discount: itemDiscount || 0,
               index,
               item_category: productType || null,
               price: price || 0,
               quantity: quantity || 1,
          };

          //Normal Product Except Live Training
          if (!isLiveTraining) {
               newItems.push(newItem);
               index++;
               continue;
          }

          //Normal Event
          if (eventId) {
               const eventName = product.events.find((event) => event.id === eventId)?.name || null;

               const creditAddedItem = {
                    ...newItem,
                    item_category2: eventName,
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //CustomDate Event
          if (isCustomDate) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'custom-date',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Event Credit
          if (isCredit) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'event-credit',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Tiers
          if (tierId) {
               const pricingTiers = tryParseJSON(product?.pricingTiers) || [];
               const tier = pricingTiers.find((tier) => tier.id === tierId);

               for (let i = 0; i < quantity; i++) {
                    const tierAddedItem = {
                         ...newItem,
                         index,
                         item_category2: `tier-${tier.name}` || null,
                         price: Number(tier.price || 0),
                         quantity: 1,
                    };

                    newItems.push(tierAddedItem);
                    index++;
               }
          }
     }

     if (!newItems.length) return cleanedEvent;

     const { total, discount } = calculateCartValues(cartItems, coupon);

     cleanedEvent.value = total;
     cleanedEvent.items = [...newItems];
     cleanedEvent.discount = calculateTotalDiscountOnCart(newItems, discount, coupon);

     pushToDataLayerLL({ cleanedEvent, coupon, discount });

     return cleanedEvent;
};

export const collectECEvent_addShippingInfo = (payload) => {
     const { cartItems, products, coupon, shippingTier, user } = payload;
     if (!cartItems.length) return;

     const isTestMode = checkingTestMode(user);
     if (isTestMode) return;

     const cleanedEvent = {
          event: ECOMMERCE_EVENT.ADD_SHIPPING_INFO,
          currency: 'USD',
          discount: 0,
          value: 0,
          coupon: coupon?.code || null,
          shipping_tier: shippingTier || null,
          items: [],
     };

     const isCouponOnItem = coupon && coupon.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL;
     const validProductIds = coupon?.validProductIds || [];

     let index = 1;
     const newItems = [];

     for (let i = 0; i < cartItems.length; i++) {
          const item = cartItems[i];
          const { productId, eventId, isCredit, tierId, quantity } = item;

          const product = products.find((product) => product.id === productId);
          if (!product) continue;

          const couponOnItem = isCouponOnItem && (validProductIds.includes(productId) || !validProductIds.length) ? coupon.code : null;
          const itemDiscount = calculateTotalDiscountOnItem(item, product, coupon, []);

          const { sku, publishName, price } = product;
          const productType = product.productType.trainingProductType || null;
          const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          const isCustomDate = !eventId && !isCredit && !tierId;

          const newItem = {
               item_id: sku || null,
               item_name: publishName || null,
               coupon: couponOnItem,
               discount: itemDiscount || 0,
               index,
               item_category: productType || null,
               price: price || 0,
               quantity: quantity || 1,
          };

          //Normal Product Except Live Training
          if (!isLiveTraining) {
               newItems.push(newItem);
               index++;
               continue;
          }

          //Normal Event
          if (eventId) {
               const eventName = product.events.find((event) => event.id === eventId)?.name || null;

               const creditAddedItem = {
                    ...newItem,
                    item_category2: eventName,
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //CustomDate Event
          if (isCustomDate) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'custom-date',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Event Credit
          if (isCredit) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'event-credit',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Tiers
          if (tierId) {
               const pricingTiers = tryParseJSON(product?.pricingTiers) || [];
               const tier = pricingTiers.find((tier) => tier.id === tierId);

               for (let i = 0; i < quantity; i++) {
                    const tierAddedItem = {
                         ...newItem,
                         index,
                         item_category2: `tier-${tier.name}` || null,
                         price: Number(tier.price || 0),
                         quantity: 1,
                    };

                    newItems.push(tierAddedItem);
                    index++;
               }
          }
     }

     if (!newItems.length) return cleanedEvent;

     const { total, discount } = calculateCartValues(cartItems, coupon);

     cleanedEvent.value = total;
     cleanedEvent.items = [...newItems];
     cleanedEvent.discount = calculateTotalDiscountOnCart(newItems, discount, coupon);

     pushToDataLayerLL({ cleanedEvent, coupon, discount });

     return cleanedEvent;
};

export const collectECEvent_purchase = (payload) => {
     const { order, products, coupon, user } = payload;

     const isTestMode = checkingTestModeIncludeSOW({ user, order });
     if (isTestMode) return;

     const cleanedEvent = {
          event: ECOMMERCE_EVENT.PURCHASE,
          transaction_id: order.selfId || null,
          currency: 'USD',
          discount: 0,
          value: order.total || 0,
          tax: null,
          shipping: null,
          coupon: order?.couponCode || null,
          items: [],
     };

     const isCouponOnItem = coupon && coupon.type === COUPON_TYPE.DISCOUNT_AMOUNT_IN_TOTAL;
     const validProductIds = coupon?.validProductIds || [];

     let index = 1;
     const orderItems = order.orderItems || [];
     const newItems = [];

     for (let i = 0; i < orderItems.length; i++) {
          const item = orderItems[i];
          const { productId, eventId, isCredit, tierId, price, quantity } = item;

          const product = products.find((product) => product.id === productId);
          if (!product) continue;

          const couponOnItem = isCouponOnItem && (validProductIds.includes(productId) || !validProductIds.length) ? coupon.code : null;
          const itemDiscount = calculateTotalDiscountOnItem(item, product, coupon, []);

          const { sku, publishName } = product;
          const productType = product.productType.trainingProductType || null;
          const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          const isCustomDate = !eventId && !isCredit && !tierId;

          const newItem = {
               item_id: sku || null,
               item_name: publishName || null,
               coupon: couponOnItem,
               discount: itemDiscount || 0,
               index,
               item_category: productType || null,
               price: price || 0,
               quantity: quantity || 1,
          };

          //Normal Product Except Live Training
          if (!isLiveTraining) {
               newItems.push(newItem);
               index++;
               continue;
          }

          //Normal Event
          if (eventId) {
               const eventName = product.events.find((event) => event.id === eventId)?.name || null;

               const creditAddedItem = {
                    ...newItem,
                    item_category2: eventName,
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //CustomDate Event
          if (isCustomDate) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'custom-date',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Event Credit
          if (isCredit) {
               const creditAddedItem = {
                    ...newItem,
                    item_category2: 'event-credit',
               };

               newItems.push(creditAddedItem);
               index++;
               continue;
          }

          //Tiers
          if (tierId) {
               const pricingTiers = tryParseJSON(product?.pricingTiers) || [];
               const tier = pricingTiers.find((tier) => tier.id === tierId);

               for (let i = 0; i < quantity; i++) {
                    const tierAddedItem = {
                         ...newItem,
                         index,
                         item_category2: `tier-${tier.name}` || null,
                         price: Number(tier.price || 0),
                         quantity: 1,
                    };

                    newItems.push(tierAddedItem);
                    index++;
               }
          }
     }

     if (!newItems.length) return cleanedEvent;

     const { discount } = calculateCartValues(orderItems, coupon);

     cleanedEvent.items = [...newItems];
     cleanedEvent.discount = calculateTotalDiscountOnCart(newItems, discount, coupon);

     pushToDataLayerLL({ cleanedEvent, coupon, discount });

     return cleanedEvent;
};
